/**
 * Resolve baked descriptor media paths for HTML paint.
 */
import type { PublicShareManifest } from '@containd/shared';
import type { RenderDescriptor } from '@containd/template-render';
import { looksLikeMediaPath } from './shareMediaUrl';
import { resolveShareCanvasRasterUrl } from './shareCanvasThumb';

function resolveToUrl(
  value: string,
  manifest: PublicShareManifest,
  options: { animateImages: boolean; preferThumb: boolean },
): string {
  if (!looksLikeMediaPath(value)) return value;
  return (
    resolveShareCanvasRasterUrl(manifest, value, options) ?? value
  );
}

function walk(
  node: RenderDescriptor,
  manifest: PublicShareManifest,
  animateImages: boolean,
  preferThumb: boolean,
): RenderDescriptor {
  const animate = node.props?.animate === true || animateImages;
  const props: Record<string, unknown> = { ...node.props };
  for (const [k, v] of Object.entries(props)) {
    if (typeof v === 'string') {
      const isRasterProp = k === 'uri' || k === 'src' || /Uri$|Url$/.test(k);
      props[k] = resolveToUrl(v, manifest, {
        animateImages: isRasterProp ? animate : true,
        preferThumb: isRasterProp ? preferThumb : false,
      });
    }
  }
  return {
    ...node,
    props,
    children: node.children?.map((c) =>
      walk(c, manifest, animateImages, preferThumb),
    ),
  };
}

/** Rewrite relative/ContaindImages paths in baked descriptors to share media URLs. */
export function resolveBakedDescriptorsMediaUrls(
  manifest: PublicShareManifest,
  descriptors: RenderDescriptor[],
  options?: { animateImages?: boolean; preferThumb?: boolean },
): RenderDescriptor[] {
  if (!manifest.media?.length) return descriptors;
  const animateImages = options?.animateImages === true;
  const preferThumb = options?.preferThumb === true;
  return descriptors.map((d) => walk(d, manifest, animateImages, preferThumb));
}
